import { API_BASE } from "../../../lib/api.ts";

export const CONTROL_POLICY_ROOT = "control-policy";
export const POLICY_JWS_LIMIT = 65_536;

export type PolicyState = "none" | "ok" | "expiring" | "expired";
export type FindingSeverity = "error" | "warning" | "note";

export interface PolicyListRow {
  readonly policy_id: string;
  readonly latest_version: number | null;
  readonly state: PolicyState;
  readonly assigned_agents: number;
  readonly tenant_default: boolean;
}
export interface PolicyListResponse { readonly policies: readonly PolicyListRow[] }

export interface PolicyFinding {
  readonly severity: FindingSeverity;
  readonly path: string;
  readonly rule: string;
  readonly code?: string;
}
export interface OperationClass {
  readonly aisClass: string;
  readonly policyClass: string;
}
export interface PolicyValidationResponse {
  readonly ok: boolean;
  readonly findings: readonly PolicyFinding[];
  readonly operation_classes: Readonly<Record<string, OperationClass>>;
  readonly acknowledgement_required: boolean;
  readonly acknowledgement_items: readonly string[];
}
export interface PolicyDraftRecord {
  readonly record_version: number;
  readonly updated_by: string;
  readonly updated_at: number;
  readonly draft: Readonly<Record<string, unknown>>;
}
export interface PolicyVersionMeta {
  readonly version: number;
  readonly digest: string;
  readonly kid: string;
  readonly issued_at: number;
  readonly expires_at: number;
  readonly supersedes: number | null;
  readonly created_by: string;
  readonly reviewed_by: string | null;
  readonly retention_class: string;
}
export interface PolicyDetailResponse {
  readonly policy_id: string;
  readonly draft: PolicyDraftRecord | null;
  readonly versions: readonly PolicyVersionMeta[];
}
export interface PolicyVersionResponse extends PolicyVersionMeta {
  readonly policy_id: string;
  readonly jws: string;
  readonly payload: Readonly<Record<string, unknown>>;
}
export interface AssignmentRow {
  readonly scope: "tenant" | "agent";
  readonly scope_key: string;
  readonly policy_id: string;
  readonly pinned_version: number | null;
}
export interface AssignmentsResponse {
  readonly record_version: number;
  readonly assignments: readonly AssignmentRow[];
}
export type AssignmentPreview =
  | { readonly kind: "none" }
  | { readonly kind: "unusable"; readonly reason: "missing" | "expired" }
  | { readonly kind: "ok"; readonly policyId: string; readonly version: number; readonly exp: number };
export interface CoverageRow {
  readonly agent_sub: string;
  readonly status: string;
  readonly reason: "no_assignment" | "expired" | "expiring";
}
export interface CoverageResponse {
  readonly noPolicy: readonly CoverageRow[];
  readonly expiringOrExpired: readonly CoverageRow[];
  readonly noPolicyCount: number;
  readonly expiringOrExpiredCount: number;
}

export type PolicyHttpMethod = "POST" | "PUT";
export type PolicyResult<T> =
  | { readonly ok: true; readonly body: T }
  | { readonly ok: false; readonly status: number; readonly code: string; readonly path?: string; readonly rule?: string };

function errorCode(body: unknown, status: number): { code: string; path?: string; rule?: string } {
  if (typeof body !== "object" || body === null) return { code: `http_${status}` };
  const record = body as Record<string, unknown>;
  const error = record["error"];
  let code = `http_${status}`;
  if (typeof error === "string") code = error;
  else if (typeof error === "object" && error !== null && typeof (error as Record<string, unknown>)["code"] === "string") {
    code = String((error as Record<string, unknown>)["code"]);
  }
  return {
    code,
    ...(typeof record["path"] === "string" ? { path: record["path"] } : {}),
    ...(typeof record["rule"] === "string" ? { rule: record["rule"] } : {}),
  };
}

/** Send a policy action without ever logging its body (drafts and signed documents stay out of ordinary logs). */
export async function controlPolicyAction<T>(
  method: PolicyHttpMethod,
  subpath: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<PolicyResult<T>> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}/service-admin/ais/${CONTROL_POLICY_ROOT}/${subpath.replace(/^\/+/, "")}`, {
      method,
      credentials: "same-origin",
      headers: { accept: "application/json", "content-type": "application/json" },
      body: JSON.stringify(body),
      ...(signal === undefined ? {} : { signal }),
    });
  } catch {
    return { ok: false, status: 0, code: "network" };
  }
  const parsed: unknown = await response.json().catch(() => null);
  if (response.ok) return { ok: true, body: parsed as T };
  return { ok: false, status: response.status, ...errorCode(parsed, response.status) };
}

/** Plain, secret-free operator text. Unknown server codes are intentionally not reflected into the DOM. */
export function policyErrorMessage(code: string): string {
  switch (code) {
    case "network": return "The request could not be sent. Check your connection and try again.";
    case "mfa_required": return "Enter a current two-factor code to continue.";
    case "mfa_enrollment_required": return "Set up two-factor authentication first. This action requires a fresh second factor.";
    case "service_auth_failed": return "The control plane could not authenticate to the AIS. Nothing was changed.";
    case "service_unavailable":
    case "policy_unavailable": return "Policy administration is temporarily unavailable. Nothing was changed.";
    case "policy_not_found": return "That policy is not available in this tenant.";
    case "version_conflict": return "The policy or assignment set changed. Reload it before trying again.";
    case "assignment_conflict": return "An assignment references an expired, missing, or non-latest policy version.";
    case "policy_invalid": return "The draft has a blocking validation finding.";
    case "policy_too_large": return `The signed policy exceeds the ${POLICY_JWS_LIMIT.toLocaleString()}-character limit.`;
    case "policy_unsupported_by_servers": return "The policy uses controls that servers may not support. Review and acknowledge the warning.";
    case "reviewer_required": return "A different reviewer is required for this publish.";
    case "invalid_request": return "Review the required fields and try again.";
    case "forbidden": return "Only a tenant administrator can change control policies.";
    default: return "The action could not be completed. Nothing was changed.";
  }
}

function base64urlLength(byteLength: number): number {
  return Math.ceil((byteLength * 4) / 3);
}

export interface JwsLengthEstimate {
  readonly characters: number;
  readonly limit: number;
  readonly percent: number;
  readonly level: "ok" | "warning" | "error";
}

/**
 * Conservative live estimate: base64url draft bytes plus bounded server fields, protected header,
 * separators, and the fixed Ed25519 signature. Publish remains authoritative and measures the exact JWS.
 */
export function estimateSignedJwsChars(draftText: string): JwsLengthEstimate {
  const draftBytes = new TextEncoder().encode(draftText).byteLength;
  const characters = base64urlLength(draftBytes) + 768;
  const percent = Math.min(100, (characters / POLICY_JWS_LIMIT) * 100);
  return {
    characters,
    limit: POLICY_JWS_LIMIT,
    percent,
    level: characters > POLICY_JWS_LIMIT ? "error" : characters >= POLICY_JWS_LIMIT * 0.9 ? "warning" : "ok",
  };
}
